import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBook, FaUsers, FaAward, FaHeart, FaTimes, FaPhone, FaWhatsapp } from 'react-icons/fa';
import { whatsappLink } from '../config/school';
import { useSchool } from '../contexts/SchoolContext';
import Seo, { organizationJsonLd } from '../seo/Seo';
import './Home.css';

const Home = () => {
  const { secondaryRange, settings } = useSchool();
  const phone = settings.phones[0];
  const whatsapp = settings.phones.find((p) => p.whatsapp);
  const [selectedFeature, setSelectedFeature] = useState(null);

  const features = [
    {
      id: 1,
      icon: <FaBook />,
      title: 'Quality Education',
      shortDesc: 'Comprehensive curriculum blending Montessori philosophy with modern educational practices',
      fullDesc: 'Our quality education program is designed to provide students with a well-rounded learning experience. We combine the proven Montessori method with contemporary teaching techniques to ensure each child receives the best possible education. Our curriculum covers all core subjects while encouraging critical thinking, creativity, and problem-solving skills.',
      highlights: [
        'Internationally recognized Montessori curriculum',
        'STEM-focused learning approach',
        'Hands-on practical activities',
        'Individual learning pace',
        'Regular progress assessments',
        'Integration of technology in learning'
      ]
    },
    {
      id: 2,
      icon: <FaUsers />,
      title: 'Experienced Teachers',
      shortDesc: 'Certified educators passionate about fostering each child\'s unique potential',
      fullDesc: 'Our teaching staff consists of highly qualified and certified educators with years of experience in early childhood and Secondary education. Each teacher undergoes continuous professional development to stay updated with the latest educational methodologies. They are dedicated to understanding each child\'s learning style and adapting their approach accordingly.',
      highlights: [
        'Montessori certified instructors',
        'Average 10+ years teaching experience',
        'Regular professional development',
        'Low student-teacher ratio (1:12)',
        'Specialized training in child psychology',
        'Passionate and caring mentors'
      ]
    },
    {
      id: 3,
      icon: <FaAward />,
      title: 'Holistic Development',
      shortDesc: 'Focus on academic, social, emotional, and physical growth of every child',
      fullDesc: 'We believe in nurturing the whole child. Our holistic approach ensures that students develop not just academically, but also socially, emotionally, and physically. Through a variety of activities, programs, and experiences, we help children build confidence, resilience, and essential life skills that will serve them throughout their lives.',
      highlights: [
        'Character education programs',
        'Sports and physical activities',
        'Arts and music integration',
        'Social skills development',
        'Emotional intelligence training',
        'Leadership opportunities'
      ]
    },
    {
      id: 4,
      icon: <FaHeart />,
      title: 'Caring Environment',
      shortDesc: 'Safe, supportive atmosphere where children feel valued and encouraged',
      fullDesc: 'Safety and emotional well-being are our top priorities. We have created a warm, nurturing environment where every child feels secure, respected, and loved. Our school promotes positive relationships, mutual respect, and a strong sense of community. Children are encouraged to express themselves, take healthy risks, and learn from their experiences in a supportive setting.',
      highlights: [
        '24/7 security and surveillance',
        'Clean and safe facilities',
        'Healthy meal programs',
        'Anti-bullying policies',
        'Counseling support available',
        'Strong parent-school partnership'
      ]
    }
  ];

  return (
    <div className="home">
      <Seo path="/" jsonLd={organizationJsonLd(settings)} />
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="hero-title">KS Montessori & Secondary School</h1>
            <p className="hero-subtitle">
              Nurturing curious minds. Inspiring lifelong learning.
            </p>
            <p className="hero-description">
              A caring, academically rigorous environment where every child is encouraged 
              to discover their potential through Montessori principles and modern pedagogy.
            </p>
            <div className="hero-buttons">
              <Link to="/admissions" className="btn btn-primary">Apply Now</Link>
              <Link to="/contact?type=visit_request" className="btn btn-secondary">Request a Visit</Link>
              <Link to="/about" className="btn btn-secondary">Learn More</Link>
            </div>
            {(phone || whatsapp) && (
              <p className="hero-contact">
                {phone && (
                  <a href={`tel:${phone.tel}`} className="hero-contact-link">
                    <FaPhone aria-hidden="true" /> Call {phone.display}
                  </a>
                )}
                {whatsapp && (
                  <a href={whatsappLink(whatsapp.tel)} target="_blank" rel="noopener noreferrer" className="hero-contact-link">
                    <FaWhatsapp aria-hidden="true" /> WhatsApp us
                  </a>
                )}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section features">
        <div className="container">
          <h2 className="section-title">Why Families Choose KSMS</h2>
          <p className="section-subtitle">
            A supportive community and purposeful learning that develops the whole child
          </p>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={feature.id}
                className="feature-card"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 10px 30px rgba(46, 204, 113, 0.3)",
                  transition: { duration: 0.3 }
                }}
                onClick={() => setSelectedFeature(feature)}
              >
                <motion.div 
                  className="feature-icon"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  {feature.icon}
                </motion.div>
                <h3>{feature.title}</h3>
                <p>{feature.shortDesc}</p>
                <span className="learn-more-btn">Click to learn more →</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Overview */}
      <section className="section programs-preview">
        <div className="container">
          <h2 className="section-title">Programs at a Glance</h2>
          <p className="section-subtitle">
            Age-appropriate pathways that foster curiosity, confidence, and strong foundations
          </p>

          <div className="programs-grid">
            <div className="program-card">
              <h3>Montessori (Ages 3-6)</h3>
              <p>
                Child-centered learning environment promoting independence, 
                exploration, and hands-on learning through Montessori materials
              </p>
              <Link to="/programs" className="learn-more">Learn More →</Link>
            </div>

            <div className="program-card">
              <h3>Secondary{secondaryRange ? ` (${secondaryRange})` : ''}</h3>
              <p>
                Structured curriculum building strong foundations in core subjects 
                while encouraging critical thinking and creativity
              </p>
              <Link to="/programs" className="learn-more">Learn More →</Link>
            </div>

            <div className="program-card">
              <h3>Extended Care</h3>
              <p>
                Before and after-school programs providing a safe, engaging 
                environment with homework support and enrichment activities
              </p>
              <Link to="/programs" className="learn-more">Learn More →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Join Our School Community?</h2>
          <p>Begin your child’s educational journey with care and excellence.</p>
          <Link to="/admissions" className="btn btn-primary">Start Application</Link>
        </div>
      </section>

      {/* Feature Details Modal */}
      <AnimatePresence>
        {selectedFeature && (
          <motion.div 
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedFeature(null)}
          >
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.5, opacity: 0, y: 100 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.5, opacity: 0, y: 100 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close" onClick={() => setSelectedFeature(null)}>
                <FaTimes />
              </button>
              
              <motion.div 
                className="modal-icon"
                initial={{ scale: 0 }}
                animate={{ scale: 1, rotate: 360 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                {selectedFeature.icon}
              </motion.div>
              
              <h2>{selectedFeature.title}</h2>
              
              <motion.p 
                className="modal-description"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {selectedFeature.fullDesc}
              </motion.p>
              
              <motion.div 
                className="modal-highlights"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <h3>Key Highlights:</h3>
                <ul>
                  {selectedFeature.highlights.map((highlight, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + (index * 0.1) }}
                    >
                      {highlight}
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <Link to="/about" className="btn btn-primary" onClick={() => setSelectedFeature(null)}>
                  Learn More About Us
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Home;
